import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View, Text, ScrollView, StyleSheet, ActivityIndicator, TouchableOpacity,
  TextInput, Modal, Pressable, Image, Animated, Easing, useWindowDimensions,
} from 'react-native';
import { collection, onSnapshot, query, orderBy, doc, updateDoc, deleteDoc, getDocs, getDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { db } from '../firebase/firebase';
import Helper from '../utills/Helper';
import RestaurantOrderMapModal from './RestaurantOrderMapModal';
import { generateSummaryPdf } from '../utills/statementPdf';
import { getUserToken, sendPushNotification } from '../query/notificationService';

/**
 * Live order screen for the `zapee_restaurant_orders` collection.
 * Feature-parity with the Live Orders tab: schedule date navigation,
 * filter sheet, Excel export, full route/assign map modal, items modal
 * and inline status updates. Standalone — does not touch the legacy
 * `Orders` collection used by SupportOrder.tsx.
 */

const COLLECTION = 'zapee_restaurant_orders';

// Material icon helper (font is loaded globally — same as the rest of the app)
const MIcon = ({ name, size = 18, color = '#6B7280', style }: { name: string; size?: number; color?: string; style?: any }) => (
  <span className="material-icons-round" style={{ fontSize: size, color, lineHeight: 1, verticalAlign: 'middle', ...style }}>{name}</span>
);

const STATUS_COLOR: Record<string, string> = {
  DELIVERED: '#10b981',
  CANCELLED: '#ef4444',
  CANCEL: '#ef4444',
  PENDING: '#f59e0b',
  CONFIRMED: '#3b82f6',
  ACCEPTED: '#3b82f6',
  PROCESSING: '#8b5cf6',
  PICKED: '#8b5cf6',
  PREPARING: '#8b5cf6',
  READY: '#14b8a6',
};

interface RItem {
  count?: number;
  image?: string;
  isVeg?: boolean;
  mrp?: number;
  name?: string;
  price?: number;
  productId?: string;
  productName?: string;
  quantity?: number;
  sellerId?: string;
  unitPrice?: number;
}

interface ROrder {
  id: string;
  active?: boolean;
  branchId?: string;
  customerId?: string;
  customerPaid?: number;
  distance?: number;
  status?: string;
  totalPrice?: number;
  createdAt?: any;
  updatedAt?: any;
  orderNumber?: string[];
  deliveryPartnerId?: string;
  deliveryPartnerStatus?: string;
  sellerId?: string;
  charges?: { deliveryCharge?: number; packing?: number; platformFee?: number; surge?: number };
  payment?: { amount?: number; method?: string; paymentId?: string; status?: string };
  orderItems?: RItem[];
  userLocation?: { name?: string; address?: string; contact?: string; secondaryContact?: string; latitude?: number; longitude?: number };
  deliveryLocation?: { name?: string; address?: string; contact?: string; secondaryContact?: string; latitude?: number; longitude?: number };
  sellerLocation?: { name?: string; address?: string; contact?: string; latitude?: number; longitude?: number };
}

const STATUS_OPTIONS = ['ALL', 'DELIVERED', 'CANCELLED'];
const SCHEDULES: Array<'DAILY' | 'WEEKLY' | 'MONTHLY' | 'CUSTOM'> = ['DAILY', 'WEEKLY', 'MONTHLY', 'CUSTOM'];

function toDate(ts: any): Date | null {
  if (!ts) return null;
  try {
    const d = ts?.toDate ? ts.toDate() : (typeof ts === 'number' ? new Date(ts) : new Date(String(ts)));
    return isNaN(d.getTime()) ? null : d;
  } catch { return null; }
}

// Promo helpers — the order doc carries an optional `promo` block (free
// delivery + offer) written by the customer app.
function promoOf(o: ROrder): any { return (o as any).promo || {}; }
function freeDelAmt(o: ROrder): number {
  const p = promoOf(o);
  return p.freeDelivery ? Number(p.freeDeliveryAmount || 0) : 0;
}
function offerAmt(o: ROrder): number { return Number(promoOf(o).discountAmount || 0); }

// Product amount = sum of the ordered items (qty × unit price), i.e. the food
// subtotal WITHOUT delivery/packing/platform/surge. `totalPrice` is the grand
// total, so the product amount must be computed from the items.
function productAmount(o: ROrder): number {
  return (o.orderItems || []).reduce((s, it) => {
    const qty = Number((it as any).quantity ?? (it as any).count ?? 1) || 0;
    const pr = Number((it as any).unitPrice ?? (it as any).price ?? 0) || 0;
    return s + qty * pr;
  }, 0);
}

// Amount to settle to the restaurant = product amount after the 20% deduction.
function settleAmt(o: ROrder): number { return productAmount(o) * 0.8; }

// Column style helpers for the per-entity summary modal.
const SUM_HEAD = (w: number, align: any = 'right'): any => ({ width: w, fontSize: 10, fontWeight: '700', color: '#64748B', textTransform: 'uppercase', textAlign: align, paddingHorizontal: 6 });
const SUM_CELL = (w: number, align: any = 'right'): any => ({ width: w, fontSize: 11, color: '#334155', textAlign: align, paddingHorizontal: 6 });

// Columns for the per-entity summary — shared by the modal and its PDF export.
// Partner "Collection" is the CASH (COD) collected, not online-paid amounts.
function getSummaryCols(mode: 'restaurant' | 'partner'): { l: string; v: (r: any) => number; dist?: boolean }[] {
  return mode === 'restaurant'
    ? [
        { l: 'Total', v: r => r.total },
        { l: 'Product', v: r => r.product },
        { l: 'Settle', v: r => r.settle },
        { l: 'Charges', v: r => r.delivery + r.packing + r.platform + r.surge },
        { l: 'Free Del', v: r => r.freeDelivery },
        { l: 'Offers', v: r => r.offer },
        { l: 'Collection', v: r => r.total - r.offer - r.freeDelivery },
      ]
    : [
        { l: 'Delivery', v: r => r.delivery },
        { l: 'Surge', v: r => r.surge },
        { l: 'Zapee +50', v: r => r.zapeeExtra },
        { l: 'Offers', v: r => r.offer },
        { l: 'Free Del', v: r => r.freeDelivery },
        { l: 'Collection', v: r => r.cash },
        { l: 'Dist (km)', v: r => r.distance, dist: true },
      ];
}

// Full order id / number.
function lastOrderNo(o: ROrder): string {
  const raw = o.orderNumber?.[0];
  return raw ? String(raw) : (o.id || 'N/A');
}

function fmtDate(ts: any): string {
  const d = toDate(ts);
  if (!d) return 'N/A';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function inPeriod(
  d: Date,
  schedule: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'CUSTOM',
  cal: Date,
  customStart?: string,
  customEnd?: string
): boolean {
  if (schedule === 'CUSTOM') {
    if (!customStart && !customEnd) return true;
    const start = customStart ? new Date(customStart + 'T00:00:00') : new Date(0);
    const end = customEnd ? new Date(customEnd + 'T23:59:59.999') : new Date(8640000000000000);
    return d >= start && d <= end;
  }
  if (schedule === 'DAILY') {
    return d.getFullYear() === cal.getFullYear() && d.getMonth() === cal.getMonth() && d.getDate() === cal.getDate();
  }
  if (schedule === 'WEEKLY') {
    const start = new Date(cal); start.setDate(cal.getDate() - cal.getDay()); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(start.getDate() + 6); end.setHours(23, 59, 59, 999);
    return d >= start && d <= end;
  }
  return d.getFullYear() === cal.getFullYear() && d.getMonth() === cal.getMonth();
}

export default function RestaurantOrdersScreen() {
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const [orders, setOrders] = useState<ROrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sellerFilter, setSellerFilter] = useState('ALL');
  const [dpFilter, setDpFilter] = useState('ALL');
  const [paymentFilter, setPaymentFilter] = useState('ALL');
  const [partnerNames, setPartnerNames] = useState<Record<string, string>>({});
  const [schedule, setSchedule] = useState<'DAILY' | 'WEEKLY' | 'MONTHLY' | 'CUSTOM'>('DAILY');
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [calendar, setCalendar] = useState(new Date());
  const [filterModal, setFilterModal] = useState(false);
  const [itemsModalOrder, setItemsModalOrder] = useState<ROrder | null>(null);
  const [mapOrder, setMapOrder] = useState<ROrder | null>(null);
  const [profileOrder, setProfileOrder] = useState<ROrder | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [statusMenuId, setStatusMenuId] = useState<string | null>(null);

  // ── Live listener ────────────────────────────────────────────
  useEffect(() => {
    setLoading(true);
    let unsub: (() => void) | undefined;
    const attach = (useOrder: boolean): (() => void) => {
      const ref = useOrder
        ? query(collection(db, COLLECTION), orderBy('createdAt', 'desc'))
        : collection(db, COLLECTION);
      return onSnapshot(
        ref as any,
        (snap: any) => {
          setOrders(snap.docs.map((d: any) => ({ id: d.id, ...d.data() })) as ROrder[]);
          setLoading(false);
          setError(null);
        },
        (e: any) => {
          if (useOrder) { console.warn('Ordered listener failed, retrying unordered:', e); unsub = attach(false); }
          else { console.error(e); setError('Failed to load orders'); setLoading(false); }
        }
      );
    };
    try { unsub = attach(true); }
    catch (e) { console.error(e); setError('Failed to load orders'); setLoading(false); }
    return () => { if (unsub) unsub(); };
  }, []);

  // ── One-time delivery-partner name lookup (for the DP filter labels) ──
  useEffect(() => {
    getDocs(collection(db, 'zapee_delivery_partners'))
      .then(snap => {
        const m: Record<string, string> = {};
        snap.docs.forEach(d => {
          const x = d.data() as any;
          const label = x.name || x.email || x.phone || d.id;
          if (x.partnerId) m[x.partnerId] = label;
          m[d.id] = label;
        });
        setPartnerNames(m);
      })
      .catch(() => { /* labels just fall back to the raw id */ });
  }, []);

  // ── Period (schedule) scoped orders ──────────────────────────
  const periodOrders = useMemo(
    () => orders.filter(o => {
      const d = toDate(o.createdAt);
      return d ? inPeriod(d, schedule, calendar, customStartDate, customEndDate) : false;
    }),
    [orders, schedule, calendar, customStartDate, customEndDate]
  );

  // ── Filter option lists (derived from the current period) ────
  const availableSellers = useMemo(() => {
    const m = new Map<string, string>();
    periodOrders.forEach(o => { if (o.sellerId) m.set(o.sellerId, o.sellerLocation?.name || o.sellerId); });
    return Array.from(m, ([id, name]) => ({ id, name }));
  }, [periodOrders]);

  const availablePartners = useMemo(() => {
    const s = new Set<string>();
    periodOrders.forEach(o => { if (o.deliveryPartnerId) s.add(o.deliveryPartnerId); });
    return Array.from(s).map(id => ({ id, name: partnerNames[id] || id }));
  }, [periodOrders, partnerNames]);

  const [summaryMode, setSummaryMode] = useState<null | 'restaurant' | 'partner'>(null);

  // ── Order detail drawer ─────────────────────────────────────
  const [detailOrder, setDetailOrder] = useState<ROrder | null>(null);
  const detailW = Math.min(isMobile ? width : 480, width);
  const detailX = useRef(new Animated.Value(detailW)).current;
  useEffect(() => {
    if (detailOrder) {
      detailX.setValue(detailW);
      Animated.timing(detailX, { toValue: 0, duration: 260, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
    }
  }, [detailOrder, detailW, detailX]);
  const openDetail = (o: ROrder) => setDetailOrder(o);
  const closeDetail = () => Animated.timing(detailX, { toValue: detailW, duration: 200, easing: Easing.in(Easing.cubic), useNativeDriver: true }).start(() => setDetailOrder(null));

  const availablePayments = useMemo(() => {
    const s = new Set<string>();
    periodOrders.forEach(o => { if (o.payment?.method) s.add(o.payment.method); });
    return Array.from(s);
  }, [periodOrders]);

  // ── Status + search filter ───────────────────────────────────
  const visible = useMemo(() => {
    const lower = searchQuery.trim().toLowerCase();
    return periodOrders.filter(o => {
      if (statusFilter !== 'ALL' && (o.status || '').toUpperCase() !== statusFilter) return false;
      if (sellerFilter !== 'ALL' && o.sellerId !== sellerFilter) return false;
      if (dpFilter !== 'ALL' && o.deliveryPartnerId !== dpFilter) return false;
      if (paymentFilter !== 'ALL' && o.payment?.method !== paymentFilter) return false;
      if (!lower) return true;
      return (
        (o.orderNumber || []).some(n => (n || '').toLowerCase().includes(lower)) ||
        (o.userLocation?.name || '').toLowerCase().includes(lower) ||
        (o.userLocation?.secondaryContact || '').includes(lower) ||
        (o.sellerLocation?.name || '').toLowerCase().includes(lower) ||
        (o.customerId || '').toLowerCase().includes(lower)
      );
    });
  }, [periodOrders, searchQuery, statusFilter, sellerFilter, dpFilter, paymentFilter]);

  // ── Per-entity summary (grouped by restaurant or delivery partner) ──
  const summaryData = useMemo(() => {
    if (!summaryMode) return null;
    const map = new Map<string, any>();
    visible.forEach(o => {
      if (summaryMode === 'partner' && !o.deliveryPartnerId) return;
      const id = (summaryMode === 'restaurant' ? o.sellerId : o.deliveryPartnerId) || '—';
      const name = summaryMode === 'restaurant'
        ? (o.sellerLocation?.name || o.sellerId || '—')
        : (partnerNames[o.deliveryPartnerId || ''] || o.deliveryPartnerId || '—');
      let g = map.get(id);
      if (!g) { g = { id, name, orders: 0, total: 0, product: 0, settle: 0, delivery: 0, packing: 0, platform: 0, surge: 0, freeDelivery: 0, offer: 0, collection: 0, cash: 0, zapeeExtra: 0, distance: 0 }; map.set(id, g); }
      const method = (o.payment?.method || '').toUpperCase();
      const isCash = method.includes('COD') || method.includes('CASH');
      const dt = toDate(o.createdAt);
      // Zapee pays the partner an extra ₹50 for orders placed 9:30 PM–6 AM.
      const isNight = dt ? (dt.getHours() > 21 || (dt.getHours() === 21 && dt.getMinutes() >= 30) || dt.getHours() < 6) : false;
      g.orders += 1;
      g.total += o.totalPrice || 0;
      g.product += productAmount(o);
      g.settle += settleAmt(o);
      g.delivery += o.charges?.deliveryCharge || 0;
      g.packing += o.charges?.packing || 0;
      g.platform += o.charges?.platformFee || 0;
      g.surge += o.charges?.surge || 0;
      if ((o.status || '').toUpperCase() === 'DELIVERED') g.freeDelivery += freeDelAmt(o);
      g.offer += offerAmt(o);
      g.collection += o.customerPaid || 0;
      g.cash += isCash ? (o.customerPaid || 0) : 0;
      g.zapeeExtra += isNight ? 50 : 0;
      g.distance += o.distance || 0;
    });
    const rows = Array.from(map.values()).sort((a, b) => b.total - a.total);
    const totals = rows.reduce((t, r) => ({
      orders: t.orders + r.orders, total: t.total + r.total, product: t.product + r.product, settle: t.settle + r.settle,
      delivery: t.delivery + r.delivery, packing: t.packing + r.packing, platform: t.platform + r.platform,
      surge: t.surge + r.surge, freeDelivery: t.freeDelivery + r.freeDelivery, offer: t.offer + r.offer,
      collection: t.collection + r.collection, cash: t.cash + r.cash, zapeeExtra: t.zapeeExtra + r.zapeeExtra, distance: t.distance + r.distance,
    }), { orders: 0, total: 0, product: 0, settle: 0, delivery: 0, packing: 0, platform: 0, surge: 0, freeDelivery: 0, offer: 0, collection: 0, cash: 0, zapeeExtra: 0, distance: 0 });
    return { rows, totals };
  }, [summaryMode, visible, partnerNames]);

  const downloadSummaryPdf = () => {
    if (!summaryData || !summaryMode) return;
    const cols = getSummaryCols(summaryMode);
    const cell = (c: any, r: any) => c.dist ? c.v(r).toFixed(1) : (c.v(r) || 0).toFixed(0);
    const rows = summaryData.rows.map((r: any) => [r.name, String(r.orders), ...cols.map(c => cell(c, r))]);
    const totals = [`TOTAL (${summaryData.rows.length})`, String(summaryData.totals.orders), ...cols.map(c => cell(c, summaryData.totals))];
    generateSummaryPdf({
      heading: summaryMode === 'restaurant' ? 'Restaurant Totals' : 'Delivery Partner Totals',
      periodLabel: `Period: ${Helper.formatDate(calendar, schedule)}`,
      entityLabel: summaryMode === 'restaurant' ? 'Restaurant' : 'Partner',
      columns: cols.map(c => ({ label: c.l })),
      rows,
      totals,
    });
  };

  // ── Summary over the FILTERED set (reflects every active filter) ──
  const summary = useMemo(() => {
    let product = 0, delivery = 0, packing = 0, platform = 0, paid = 0, delivered = 0, deliveredAmt = 0, distance = 0, freeDelivery = 0, offer = 0, settle = 0;
    visible.forEach(o => {
      product += productAmount(o);
      settle += settleAmt(o);
      delivery += o.charges?.deliveryCharge || 0;
      packing += o.charges?.packing || 0;
      platform += o.charges?.platformFee || 0;
      paid += o.customerPaid || 0;
      distance += o.distance || 0;
      offer += offerAmt(o);
      if ((o.status || '').toUpperCase() === 'DELIVERED') { delivered++; deliveredAmt += o.customerPaid || 0; freeDelivery += freeDelAmt(o); }
    });
    return { count: visible.length, product, delivery, packing, platform, paid, delivered, deliveredAmt, distance, freeDelivery, offer, settle };
  }, [visible]);

  // ── Customer profile (aggregated across ALL of this customer's orders) ──
  const profile = useMemo(() => {
    if (!profileOrder) return null;
    const cid = profileOrder.customerId;
    const cOrders = orders.filter(o => (cid ? o.customerId === cid : o.id === profileOrder.id));
    const sorted = [...cOrders].sort(
      (a, b) => (toDate(b.createdAt)?.getTime() || 0) - (toDate(a.createdAt)?.getTime() || 0)
    );
    const latest = sorted[0] || profileOrder;
    const deliveredOrders = cOrders.filter(o => (o.status || '').toUpperCase() === 'DELIVERED');
    // Spent counts only delivered orders.
    const totalSpent = deliveredOrders.reduce((s, o) => s + (o.customerPaid || 0), 0);
    const delivered = deliveredOrders.length;
    const cancelled = cOrders.filter(o => ['CANCELLED', 'CANCEL'].includes((o.status || '').toUpperCase())).length;
    return {
      cid, loc: latest.userLocation, orders: sorted, count: cOrders.length,
      totalSpent, delivered, cancelled, lastOrderAt: latest.createdAt,
    };
  }, [profileOrder, orders]);

  // Slide the profile drawer in from the right whenever it opens
  const drawerX = useRef(new Animated.Value(420)).current;
  useEffect(() => {
    if (profileOrder) {
      drawerX.setValue(420);
      Animated.timing(drawerX, { toValue: 0, duration: 260, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
    }
  }, [profileOrder, drawerX]);

  // Fetch the customer's registered display name from the `users` collection
  const [profileName, setProfileName] = useState<string | null>(null);
  useEffect(() => {
    const cid = profileOrder?.customerId;
    if (!cid) { setProfileName(null); return; }
    let active = true;
    setProfileName(null);
    getDoc(doc(db, 'users', cid))
      .then(snap => {
        if (!active) return;
        const u = snap.exists() ? (snap.data() as any) : null;
        setProfileName(u?.username || u?.displayName || u?.name || null);
      })
      .catch(() => { if (active) setProfileName(null); });
    return () => { active = false; };
  }, [profileOrder]);

  // Notify the customer we couldn't reach them — names the delivery partner
  // and the order, and deep-links to the order in the customer app.
  const [notifying, setNotifying] = useState(false);
  const notifyCustomer = async () => {
    if (!profileOrder) return;
    const order = profile?.orders?.[0] ?? profileOrder;
    const cid = profileOrder.customerId;
    if (!cid) { window.alert('No customer id on this order.'); return; }
    const partnerName =
      (order.deliveryPartnerId && partnerNames[order.deliveryPartnerId]) || 'your delivery partner';
    const orderNo = lastOrderNo(order);
    setNotifying(true);
    try {
      const fcmToken = await getUserToken(cid);
      if (!fcmToken) { window.alert('This customer has no notification token — cannot notify.'); return; }
      await sendPushNotification({
        fcmToken,
        title: 'We are unable to reach you',
        body: `Delivery partner ${partnerName} is trying to deliver your order #${orderNo}. Please pick up the call or be available to receive it.`,
        screen: 'order_details',
        orderId: order.id,
      });
      window.alert('Customer notified.');
    } catch (e) {
      window.alert('Failed to notify: ' + ((e as Error).message || 'unknown error'));
    } finally {
      setNotifying(false);
    }
  };

  // ── Date navigation ──────────────────────────────────────────
  const changeDate = (amount: number) => {
    const d = new Date(calendar);
    if (schedule === 'DAILY') d.setDate(d.getDate() + amount);
    else if (schedule === 'WEEKLY') d.setDate(d.getDate() + amount * 7);
    else d.setMonth(d.getMonth() + amount);
    setCalendar(d);
  };
  const isLastDate = () => {
    const today = new Date();
    if (schedule === 'DAILY') return calendar.toDateString() === today.toDateString();
    if (schedule === 'WEEKLY') {
      const s = (x: Date) => { const c = new Date(x); c.setDate(c.getDate() - c.getDay()); c.setHours(0, 0, 0, 0); return c.getTime(); };
      return s(calendar) === s(today);
    }
    return calendar.getFullYear() === today.getFullYear() && calendar.getMonth() === today.getMonth();
  };

  const fmtShort = (iso: string) => {
    const d = new Date(iso + 'T00:00:00');
    return `${d.getDate()} ${d.toLocaleDateString('en-GB', { month: 'short' })}`;
  };

  const getDateLabel = () => {
    if (schedule === 'CUSTOM') {
      if (customStartDate && customEndDate) return `${fmtShort(customStartDate)} → ${fmtShort(customEndDate)}`;
      if (customStartDate) return `From ${fmtShort(customStartDate)}`;
      if (customEndDate) return `Until ${fmtShort(customEndDate)}`;
      return 'Custom Range';
    }
    return Helper.formatDate(calendar, schedule);
  };

  const activeFilterCount = [
    statusFilter !== 'ALL',
    schedule !== 'DAILY',
    sellerFilter !== 'ALL',
    dpFilter !== 'ALL',
    paymentFilter !== 'ALL',
    !!(schedule === 'CUSTOM' && (customStartDate || customEndDate)),
  ].filter(Boolean).length;

  // ── Excel export ─────────────────────────────────────────────
  const exportExcel = () => {
    if (visible.length === 0) { alert('No orders available to export.'); return; }
    const data = visible.map(o => ({
      OrderID: o.orderNumber?.[0] || '',
      Customer: o.userLocation?.name || '',
      Phone: o.userLocation?.secondaryContact || '',
      Address: o.userLocation?.address || '',
      Seller: o.sellerLocation?.name || '',
      Status: o.status || '',
      Product: o.totalPrice || 0,
      Delivery: o.charges?.deliveryCharge || 0,
      Packing: o.charges?.packing || 0,
      Platform: o.charges?.platformFee || 0,
      Surge: o.charges?.surge || 0,
      CustomerPaid: o.customerPaid || 0,
      Distance: o.distance || 0,
      Payment: o.payment?.method || '',
      CreatedAt: toDate(o.createdAt)?.toLocaleString() || '',
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'RestaurantOrders');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `RestaurantOrders_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const updateStatus = async (o: ROrder, status: string) => {
    setBusyId(o.id);
    setStatusMenuId(null);
    try {
      await updateDoc(doc(db, COLLECTION, o.id), {
        status,
        active: status !== 'DELIVERED' && status !== 'CANCELLED',
      });
    } catch (e) {
      console.error('Failed to update status:', e);
      alert('Failed to update order status');
    } finally {
      setBusyId(null);
    }
  };

  // Delete an order — guarded by a passcode so it can't be done accidentally.
  const DELETE_PASSCODE = '783129';
  const deleteOrder = async (o: ROrder) => {
    setStatusMenuId(null);
    const code = typeof window !== 'undefined'
      ? window.prompt(`Delete order ${(o as any).orderNo ?? o.id}?\nEnter passcode to confirm:`)
      : null;
    if (code === null) return;            // cancelled
    if (code.trim() !== DELETE_PASSCODE) { window.alert('Incorrect passcode — order not deleted.'); return; }
    setBusyId(o.id);
    try {
      await deleteDoc(doc(db, COLLECTION, o.id));
    } catch (e) {
      console.error('Failed to delete order:', e);
      window.alert('Failed to delete order');
    } finally {
      setBusyId(null);
    }
  };

  // Delete a single product from an order — guarded by the same passcode.
  // Recomputes the order amount (totalPrice + customerPaid) and saves it.
  const deleteOrderItem = async (o: ROrder, idx: number) => {
    const items = o.orderItems || [];
    const item = items[idx];
    if (!item) return;
    const name = item.productName ?? item.name ?? 'this item';
    const code = typeof window !== 'undefined'
      ? window.prompt(`Remove "${name}" from order ${o.orderNumber?.[0] ?? o.id}?\nEnter passcode to confirm:`)
      : null;
    if (code === null) return;            // cancelled
    if (code.trim() !== DELETE_PASSCODE) { window.alert('Incorrect passcode — item not removed.'); return; }

    const lineTotal = (item.price ?? item.unitPrice ?? 0) * (item.quantity ?? item.count ?? 1);
    const newItems = items.filter((_, i) => i !== idx);
    const newTotal = Math.max(0, (o.totalPrice || 0) - lineTotal);
    const newPaid = Math.max(0, (o.customerPaid || 0) - lineTotal);

    setBusyId(o.id);
    try {
      await updateDoc(doc(db, COLLECTION, o.id), {
        orderItems: newItems,
        totalPrice: newTotal,
        customerPaid: newPaid,
      });
      // Reflect immediately in the open modal (orders list refreshes via onSnapshot).
      setItemsModalOrder(prev =>
        prev && prev.id === o.id
          ? { ...prev, orderItems: newItems, totalPrice: newTotal, customerPaid: newPaid }
          : prev);
    } catch (e) {
      console.error('Failed to remove item:', e);
      window.alert('Failed to remove item');
    } finally {
      setBusyId(null);
    }
  };

  const statusPill = (status?: string) => {
    const color = STATUS_COLOR[(status || '').toUpperCase()] ?? '#6B7280';
    return (
      <View style={[styles.statusPill, { backgroundColor: color + '18', borderColor: color + '40' }]}>
        <View style={[styles.statusDot, { backgroundColor: color }]} />
        <Text style={[styles.statusPillText, { color }]}>{status ?? 'N/A'}</Text>
      </View>
    );
  };

  const statusMenuOrder = statusMenuId ? orders.find(o => o.id === statusMenuId) ?? null : null;

  // ── Mobile card ──────────────────────────────────────────────
  const renderCard = (o: ROrder) => {
    const color = STATUS_COLOR[(o.status || '').toUpperCase()] ?? '#6B7280';
    return (
      <View key={o.id} style={styles.card}>
        <View style={[styles.cardAccent, { backgroundColor: color }]} />
        <View style={styles.cardInner}>
          <Pressable style={styles.cardHeader} onPress={() => setProfileOrder(o)}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <MIcon name="account_circle" size={18} color="#1D4ED8" />
              <Text style={styles.cardOrderNum}>#{lastOrderNo(o)}</Text>
            </View>
            {statusPill(o.status)}
          </Pressable>
          <View style={styles.cardGrid}>
            <View style={styles.cardGridItem}>
              <Text style={styles.cardLabel}>Customer</Text>
              <Text style={styles.cardValue} numberOfLines={1}>{o.userLocation?.name ?? 'N/A'}</Text>
            </View>
            <View style={styles.cardGridItem}>
              <Text style={styles.cardLabel}>Contact</Text>
              <Text style={styles.cardValue}>{o.userLocation?.secondaryContact ?? 'N/A'}</Text>
            </View>
            <View style={styles.cardGridItem}>
              <Text style={styles.cardLabel}>Total</Text>
              <Text style={[styles.cardValue, { color: '#10B981', fontWeight: '700' }]}>₹{(o.totalPrice || 0).toFixed(2)}</Text>
            </View>
            <View style={styles.cardGridItem}>
              <Text style={styles.cardLabel}>Paid</Text>
              <Text style={styles.cardValue}>₹{(o.customerPaid || 0).toFixed(2)}</Text>
            </View>
          </View>
          <View style={styles.cardRow}><Text style={styles.cardLabel}>Seller</Text><Text style={styles.cardValue} numberOfLines={1}>{o.sellerLocation?.name ?? 'N/A'}</Text></View>
          <View style={styles.cardRow}><Text style={styles.cardLabel}>Drop</Text><Text style={styles.cardValue} numberOfLines={2}>{o.userLocation?.address ?? 'N/A'}</Text></View>
          <View style={styles.cardRow}><Text style={styles.cardLabel}>Created</Text><Text style={styles.cardValue}>{fmtDate(o.createdAt)}</Text></View>
          <View style={styles.cardActions}>
            <TouchableOpacity style={styles.expandButton} onPress={() => setItemsModalOrder(o)}>
              <MIcon name="shopping_bag" size={14} color="#3B82F6" style={{ marginRight: 4 }} />
              <Text style={styles.expandButtonText}>Items ({o.orderItems?.length ?? 0})</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.mapButton} onPress={() => setMapOrder(o)}>
              <MIcon name="map" size={14} color="#10B981" style={{ marginRight: 4 }} />
              <Text style={styles.mapButtonText}>Map</Text>
            </TouchableOpacity>
            <TouchableOpacity style={styles.statusBtn} onPress={() => setStatusMenuId(o.id)} disabled={busyId === o.id}>
              {busyId === o.id ? <ActivityIndicator size="small" color="#6B7280" /> : <MIcon name="info_outline" size={20} color="#EF4444" />}
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  };

  // ── Desktop row ──────────────────────────────────────────────
  const renderRow = (o: ROrder, i: number) => (
    <View key={o.id} style={[styles.tableRow, i % 2 === 1 && styles.tableRowAlt]}>
      <Pressable style={styles.rowData} onPress={() => setProfileOrder(o)}>
        <Text style={[styles.tableCell, styles.cellSno]}>{i + 1}</Text>
        <Text style={[styles.tableCell, styles.cellOrder]} numberOfLines={1}>{o.sellerLocation?.name ?? '—'}</Text>
        <View style={[styles.cellStatus, { alignItems: 'center' }]}>{statusPill(o.status)}</View>
        <Text style={[styles.tableCell, styles.cellDate]}>{fmtDate(o.createdAt)}</Text>
        <Text style={[styles.tableCell, styles.cellPrice, { color: '#10B981', fontWeight: '600' }]}>₹{productAmount(o).toFixed(0)}</Text>
        <Text style={[styles.tableCell, styles.cellPrice, { color: '#059669', fontWeight: '700' }]}>₹{settleAmt(o).toFixed(0)}</Text>
        <Text style={[styles.tableCell, styles.cellPrice]}>₹{(o.charges?.packing || 0).toFixed(0)}</Text>
        <Text style={[styles.tableCell, styles.cellPrice]}>₹{(o.charges?.deliveryCharge || 0).toFixed(0)}</Text>
        <Text style={[styles.tableCell, styles.cellPrice]}>₹{(o.charges?.platformFee || 0).toFixed(0)}</Text>
        <Text style={[styles.tableCell, styles.cellPrice, { color: '#3B82F6' }]}>{freeDelAmt(o) > 0 ? `₹${freeDelAmt(o).toFixed(0)}` : '—'}</Text>
        <Text style={[styles.tableCell, styles.cellPrice, { color: '#8B5CF6' }]}>{offerAmt(o) > 0 ? `₹${offerAmt(o).toFixed(0)}` : '—'}</Text>
        <Text style={[styles.tableCell, styles.cellPrice]}>{o.distance != null ? o.distance.toFixed(1) : '0'}</Text>
      </Pressable>
      <View style={styles.rowActions}>
        <TouchableOpacity style={[styles.cellExpand, { alignItems: 'center' }]} onPress={() => setItemsModalOrder(o)}>
          <MIcon name="shopping_bag" size={18} color="#3B82F6" />
        </TouchableOpacity>
        <TouchableOpacity style={[styles.cellExpand, { alignItems: 'center' }]} onPress={() => setMapOrder(o)}>
          <MIcon name="map" size={18} color="#3B82F6" />
        </TouchableOpacity>
        <View style={[styles.cellExpand, { alignItems: 'center' }]}>
          <TouchableOpacity onPress={() => setStatusMenuId(o.id)} disabled={busyId === o.id}>
            {busyId === o.id ? <ActivityIndicator size="small" color="#6B7280" /> : <MIcon name="info_outline" size={18} color="#EF4444" />}
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      {/* Toolbar: title + date nav + search + filter + export */}
      <View style={styles.toolbar}>
        <TouchableOpacity style={[styles.navButton, schedule === 'CUSTOM' && { opacity: 0.3 }]} onPress={() => changeDate(-1)} disabled={schedule === 'CUSTOM'}>
          <MIcon name="chevron_left" size={20} color="#fff" />
        </TouchableOpacity>
        <TouchableOpacity style={styles.dateChip} onPress={() => setFilterModal(true)}>
          <Text style={styles.currentDate}>{getDateLabel()}</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[styles.navButton, (isLastDate() || schedule === 'CUSTOM') && { opacity: 0.3 }]} onPress={() => changeDate(1)} disabled={isLastDate() || schedule === 'CUSTOM'}>
          <MIcon name="chevron_right" size={20} color="#fff" />
        </TouchableOpacity>

        <View style={styles.searchWrap}>
          <MIcon name="search" size={15} color="#8BAAB8" style={{ marginRight: 6 }} />
          <TextInput
            placeholder="Search order #, customer, phone, seller…"
            placeholderTextColor="#8BAAB8"
            value={searchQuery}
            onChangeText={setSearchQuery}
            style={styles.toolbarSearch}
            autoCapitalize="none"
            autoCorrect={false}
          />
        </View>

        {/* Quick status chips */}
        <View style={styles.statusChipRow}>
          {STATUS_OPTIONS.map(s => {
            const active = statusFilter === s;
            const c = STATUS_COLOR[s] ?? '#1D4ED8';
            return (
              <TouchableOpacity
                key={s}
                style={[styles.statusChip, active && { backgroundColor: c, borderColor: c }]}
                onPress={() => setStatusFilter(s)}
              >
                <Text style={[styles.statusChipText, active && { color: '#fff' }]}>{s}</Text>
              </TouchableOpacity>
            );
          })}
        </View>

        <TouchableOpacity style={styles.filterButton} onPress={() => setFilterModal(true)}>
          <MIcon name="tune" size={18} color="#8BAAB8" />
          {activeFilterCount > 0 && (
            <View style={styles.filterBadge}><Text style={styles.filterBadgeText}>{activeFilterCount}</Text></View>
          )}
        </TouchableOpacity>

        <TouchableOpacity style={styles.exportButton} onPress={exportExcel}>
          <MIcon name="download" size={15} color="#fff" style={{ marginRight: 4 }} />
          <Text style={styles.exportButtonText}>Export</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.statementButton} onPress={() => setSummaryMode('restaurant')}>
          <MIcon name="storefront" size={15} color="#fff" style={{ marginRight: 4 }} />
          <Text style={styles.exportButtonText}>Restaurants</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.statementButton} onPress={() => setSummaryMode('partner')}>
          <MIcon name="two_wheeler" size={15} color="#fff" style={{ marginRight: 4 }} />
          <Text style={styles.exportButtonText}>Partners</Text>
        </TouchableOpacity>
      </View>

      {/* Content */}
      {loading ? (
        <ActivityIndicator size="large" color="#1D4ED8" style={{ marginTop: 40 }} />
      ) : error ? (
        <View style={styles.emptyBox}><MIcon name="error_outline" size={44} color="#9CA3AF" /><Text style={styles.emptyText}>{error}</Text></View>
      ) : visible.length === 0 ? (
        <View style={styles.emptyBox}><MIcon name="inbox" size={44} color="#9CA3AF" /><Text style={styles.emptyText}>No orders for this period</Text></View>
      ) : isMobile ? (
        <ScrollView style={{ marginTop: 4 }} contentContainerStyle={{ paddingBottom: 70 }}>
          {visible.map(renderCard)}
        </ScrollView>
      ) : (
        <View style={{ flex: 1, marginTop: 4 }}>
          <View style={styles.tableHeader}>
            <Text style={[styles.headerText, styles.cellSno]}>S.No</Text>
            <Text style={[styles.headerText, styles.cellOrder]}>Seller</Text>
            <Text style={[styles.headerText, styles.cellStatus]}>Status</Text>
            <Text style={[styles.headerText, styles.cellDate]}>Created</Text>
            <Text style={[styles.headerText, styles.cellPrice]}>Product</Text>
            <Text style={[styles.headerText, styles.cellPrice]}>Settle</Text>
            <Text style={[styles.headerText, styles.cellPrice]}>Packing</Text>
            <Text style={[styles.headerText, styles.cellPrice]}>Delivery</Text>
            <Text style={[styles.headerText, styles.cellPrice]}>Platform</Text>
            <Text style={[styles.headerText, styles.cellPrice]}>Free Del.</Text>
            <Text style={[styles.headerText, styles.cellPrice]}>Offer</Text>
            <Text style={[styles.headerText, styles.cellPrice]}>Dist.</Text>
            <Text style={[styles.headerText, styles.cellExpand]}>Items</Text>
            <Text style={[styles.headerText, styles.cellExpand]}>Map</Text>
            <Text style={[styles.headerText, styles.cellExpand]}>Act.</Text>
          </View>
          <ScrollView contentContainerStyle={{ paddingBottom: 70 }}>
            {visible.map((o, i) => renderRow(o, i))}
          </ScrollView>
        </View>
      )}

      {/* Fixed bottom calculation bar — totals aligned under their columns */}
      {isMobile ? (
        <View style={[styles.bottomBar, styles.bottomBarMobile]}>
          {[
            { value: String(summary.count), icon: 'receipt_long', color: '#21808D' },
            { value: `₹${summary.product.toFixed(0)}`, icon: 'lunch_dining', color: '#10B981' },
            { value: `₹${summary.packing.toFixed(0)}`, icon: 'inventory_2', color: '#F59E0B' },
            { value: `₹${summary.delivery.toFixed(0)}`, icon: 'two_wheeler', color: '#3B82F6' },
            { value: `₹${summary.platform.toFixed(0)}`, icon: 'percent', color: '#8B5CF6' },
            { value: `${summary.distance.toFixed(1)}`, icon: 'route', color: '#6B7280' },
            { value: `₹${summary.paid.toFixed(0)}`, icon: 'payments', color: '#21808D' },
          ].map((s, i) => (
            <View key={i} style={styles.bottomBarItem}>
              <MIcon name={s.icon} size={16} color={s.color} />
              <Text style={[styles.bottomBarValue, { color: s.color }]}>{s.value}</Text>
            </View>
          ))}
        </View>
      ) : (
        <View style={[styles.bottomBar, styles.bottomBarDesktop]}>
          <Text style={[styles.tableCell, styles.cellSno]} />
          <Text style={[styles.tableCell, styles.cellOrder, styles.bottomBarValue, { color: '#21808D' }]}>{summary.count} orders</Text>
          <View style={styles.cellStatus} />
          <Text style={[styles.tableCell, styles.cellDate]} />
          <Text style={[styles.tableCell, styles.cellPrice, styles.bottomBarValue, { color: '#10B981' }]}>₹{summary.product.toFixed(0)}</Text>
          <Text style={[styles.tableCell, styles.cellPrice, styles.bottomBarValue, { color: '#059669' }]}>₹{summary.settle.toFixed(0)}</Text>
          <Text style={[styles.tableCell, styles.cellPrice, styles.bottomBarValue, { color: '#F59E0B' }]}>₹{summary.packing.toFixed(0)}</Text>
          <Text style={[styles.tableCell, styles.cellPrice, styles.bottomBarValue, { color: '#3B82F6' }]}>₹{summary.delivery.toFixed(0)}</Text>
          <Text style={[styles.tableCell, styles.cellPrice, styles.bottomBarValue, { color: '#8B5CF6' }]}>₹{summary.platform.toFixed(0)}</Text>
          <Text style={[styles.tableCell, styles.cellPrice, styles.bottomBarValue, { color: '#3B82F6' }]}>₹{summary.freeDelivery.toFixed(0)}</Text>
          <Text style={[styles.tableCell, styles.cellPrice, styles.bottomBarValue, { color: '#8B5CF6' }]}>₹{summary.offer.toFixed(0)}</Text>
          <Text style={[styles.tableCell, styles.cellPrice, styles.bottomBarValue, { color: '#6B7280' }]}>{summary.distance.toFixed(1)}</Text>
          <View style={styles.cellExpand} />
          <View style={styles.cellExpand} />
          <View style={styles.cellExpand} />
        </View>
      )}

      {/* Route + Assign map modal (new zapee_delivery_partners collection) */}
      <RestaurantOrderMapModal
        visible={!!mapOrder}
        order={mapOrder as any}
        onClose={() => setMapOrder(null)}
      />

      {/* Order detail drawer */}
      <Modal visible={!!detailOrder} animationType="fade" transparent onRequestClose={closeDetail}>
        <View style={sd.root}>
          <Pressable style={sd.backdrop} onPress={closeDetail} />
          <Animated.View style={[sd.drawer, { width: detailW, transform: [{ translateX: detailX }] }]}>
            {detailOrder && (() => {
              const o = detailOrder;
              const money = (n: any) => `₹${(Number(n) || 0).toFixed(2)}`;
              const items = o.orderItems || [];
              const partner = partnerNames[o.deliveryPartnerId || ''] || o.deliveryPartnerId || '—';
              return (
                <>
                  <View style={sd.header}>
                    <TouchableOpacity style={sd.closeBtn} onPress={closeDetail}><Text style={sd.closeTxt}>✕</Text></TouchableOpacity>
                    <Text style={sd.title} numberOfLines={1}>Order #{lastOrderNo(o)}</Text>
                    <View style={{ width: 32 }} />
                  </View>
                  <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: 16 }}>
                    <View style={od.topRow}>
                      {statusPill(o.status)}
                      <TouchableOpacity style={od.custBtn} onPress={() => setProfileOrder(o)}>
                        <Text style={od.custBtnTxt}>👤 Customer history</Text>
                      </TouchableOpacity>
                    </View>

                    <Text style={od.sec}>Order Info</Text>
                    <View style={od.kv}><Text style={od.k}>Placed</Text><Text style={od.v}>{fmtDate(o.createdAt)}</Text></View>
                    <View style={od.kv}><Text style={od.k}>Payment</Text><Text style={od.v}>{o.payment?.method || '—'}{o.payment?.status ? ` · ${o.payment.status}` : ''}</Text></View>
                    {o.distance != null ? <View style={od.kv}><Text style={od.k}>Distance</Text><Text style={od.v}>{o.distance.toFixed(1)} km</Text></View> : null}

                    <Text style={od.sec}>Restaurant</Text>
                    <View style={od.kv}><Text style={od.k}>Name</Text><Text style={od.v}>{o.sellerLocation?.name || '—'}</Text></View>
                    {o.sellerLocation?.address ? <View style={od.kv}><Text style={od.k}>Address</Text><Text style={od.v}>{o.sellerLocation.address}</Text></View> : null}

                    <Text style={od.sec}>Customer</Text>
                    <View style={od.kv}><Text style={od.k}>Name</Text><Text style={od.v}>{o.userLocation?.name || o.deliveryLocation?.name || '—'}</Text></View>
                    <View style={od.kv}><Text style={od.k}>Contact</Text><Text style={od.v}>{o.userLocation?.contact || (o.userLocation as any)?.secondaryContact || o.deliveryLocation?.contact || '—'}</Text></View>
                    <View style={od.kv}><Text style={od.k}>Drop</Text><Text style={od.v}>{o.userLocation?.address || o.deliveryLocation?.address || '—'}</Text></View>

                    <Text style={od.sec}>Delivery Partner</Text>
                    <View style={od.kv}><Text style={od.k}>Name</Text><Text style={od.v}>{partner}</Text></View>
                    {o.deliveryPartnerStatus ? <View style={od.kv}><Text style={od.k}>Status</Text><Text style={od.v}>{o.deliveryPartnerStatus}</Text></View> : null}

                    <Text style={od.sec}>Items ({items.length})</Text>
                    {items.length === 0
                      ? <Text style={od.empty}>No items</Text>
                      : items.map((it: any, idx: number) => {
                          const nm = it.productName || it.name || 'Item';
                          const qty = it.quantity || it.count || 1;
                          const pr = Number(it.price ?? it.unitPrice ?? 0);
                          return (
                            <View key={idx} style={od.itemRow}>
                              {typeof it.isVeg === 'boolean' ? (
                                <View style={[od.vegDot, { borderColor: it.isVeg ? '#16A34A' : '#DC2626' }]}>
                                  <View style={[od.vegDotInner, { backgroundColor: it.isVeg ? '#16A34A' : '#DC2626' }]} />
                                </View>
                              ) : null}
                              <Text style={od.itemName} numberOfLines={1}>{nm}</Text>
                              <Text style={od.itemQty}>{qty} × ₹{pr.toFixed(0)}</Text>
                              <Text style={od.itemTot}>₹{(qty * pr).toFixed(0)}</Text>
                            </View>
                          );
                        })}

                    <Text style={od.sec}>Amounts</Text>
                    {([['Product', productAmount(o)], ['Settle (−20%)', settleAmt(o)], ['Delivery', o.charges?.deliveryCharge], ['Packing', o.charges?.packing], ['Surge', o.charges?.surge], ['Platform', o.charges?.platformFee], ['Free Delivery', freeDelAmt(o)], ['Offer', offerAmt(o)]] as [string, any][]).map(([lbl, val]) => (
                      <View key={lbl} style={od.kv}><Text style={od.k}>{lbl}</Text><Text style={od.v}>{money(val)}</Text></View>
                    ))}
                    <View style={od.totalRow}><Text style={od.totalK}>Collected</Text><Text style={od.totalV}>{money(o.customerPaid)}</Text></View>
                    <View style={{ height: 30 }} />
                  </ScrollView>
                </>
              );
            })()}
          </Animated.View>
        </View>
      </Modal>

      {/* Per-entity calculation summary (grouped per restaurant / per partner) */}
      <Modal visible={!!summaryMode} animationType="fade" transparent onRequestClose={() => setSummaryMode(null)}>
        <View style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
          <View style={{ backgroundColor: '#17201B', borderRadius: 14, width: '100%', maxWidth: 940, maxHeight: '88%', overflow: 'hidden' }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', padding: 14, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' }}>
              <MIcon name={summaryMode === 'restaurant' ? 'storefront' : 'two_wheeler'} size={20} color="#1D4ED8" />
              <Text style={{ fontSize: 15, fontWeight: '800', color: '#0f172a', marginLeft: 8, flex: 1 }}>
                {summaryMode === 'restaurant' ? 'Restaurant Totals' : 'Delivery Partner Totals'} · {Helper.formatDate(calendar, schedule)}
              </Text>
              <TouchableOpacity onPress={() => setSummaryMode(null)}><Text style={{ fontSize: 18, color: '#7A9A80' }}>✕</Text></TouchableOpacity>
            </View>
            {(() => {
              const cols = getSummaryCols(summaryMode!);
              const fmt = (c: any, r: any) => c.dist ? c.v(r).toFixed(1) : `₹${(c.v(r) || 0).toFixed(0)}`;
              return (
                <ScrollView horizontal>
                  <View>
                    <View style={{ flexDirection: 'row', backgroundColor: '#141E18', paddingVertical: 8, paddingHorizontal: 10, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' }}>
                      <Text style={SUM_HEAD(190, 'left')}>{summaryMode === 'restaurant' ? 'Restaurant' : 'Partner'}</Text>
                      <Text style={SUM_HEAD(60)}>Orders</Text>
                      {cols.map((c, ci) => <Text key={ci} style={SUM_HEAD(92)}>{c.l}</Text>)}
                    </View>
                    <ScrollView style={{ maxHeight: 460 }}>
                      {(summaryData?.rows || []).map((r: any, i: number) => (
                        <View key={r.id} style={{ flexDirection: 'row', paddingVertical: 8, paddingHorizontal: 10, backgroundColor: i % 2 ? '#141E18' : '#fff', borderBottomWidth: 1, borderBottomColor: '#F1F5F9' }}>
                          <Text style={SUM_CELL(190, 'left')} numberOfLines={1}>{r.name}</Text>
                          <Text style={SUM_CELL(60)}>{r.orders}</Text>
                          {cols.map((c, ci) => <Text key={ci} style={SUM_CELL(92)}>{fmt(c, r)}</Text>)}
                        </View>
                      ))}
                      {(summaryData?.rows || []).length === 0 && (
                        <Text style={{ padding: 20, color: '#94A3B8', textAlign: 'center' }}>No orders for this period.</Text>
                      )}
                    </ScrollView>
                    <View style={{ flexDirection: 'row', paddingVertical: 10, paddingHorizontal: 10, backgroundColor: '#EFF6FF', borderTopWidth: 1, borderTopColor: '#DBEAFE' }}>
                      <Text style={[SUM_CELL(190, 'left'), { fontWeight: '800', color: '#1D4ED8' }]}>TOTAL ({summaryData?.rows.length || 0})</Text>
                      <Text style={[SUM_CELL(60), { fontWeight: '800', color: '#1D4ED8' }]}>{summaryData?.totals.orders || 0}</Text>
                      {cols.map((c, ci) => <Text key={ci} style={[SUM_CELL(92), { fontWeight: '800', color: '#1D4ED8' }]}>{summaryData ? fmt(c, summaryData.totals) : ''}</Text>)}
                    </View>
                  </View>
                </ScrollView>
              );
            })()}
            <View style={{ flexDirection: 'row', justifyContent: 'flex-end', padding: 12, borderTopWidth: 1, borderTopColor: '#E2E8F0' }}>
              <TouchableOpacity onPress={downloadSummaryPdf} style={{ backgroundColor: '#1D4ED8', paddingHorizontal: 16, paddingVertical: 10, borderRadius: 8, flexDirection: 'row', alignItems: 'center' }}>
                <MIcon name="download" size={16} color="#fff" style={{ marginRight: 6 }} />
                <Text style={{ color: '#fff', fontWeight: '700', fontSize: 13 }}>Download PDF</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Filter modal */}
      <Modal transparent visible={filterModal} animationType="slide" onRequestClose={() => setFilterModal(false)}>
        <Pressable style={styles.filterModalOverlay} onPress={() => setFilterModal(false)}>
          <Pressable style={styles.filterModalSheet} onPress={() => {}}>
            <View style={styles.filterModalHeader}>
              <Text style={styles.filterModalTitle}>Filters</Text>
              <TouchableOpacity
                onPress={() => {
                  setStatusFilter('ALL');
                  setSchedule('DAILY');
                  setCustomStartDate('');
                  setCustomEndDate('');
                  setSellerFilter('ALL');
                  setDpFilter('ALL');
                  setPaymentFilter('ALL');
                }}
                style={styles.resetButton}
              >
                <Text style={styles.resetButtonText}>Reset</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setFilterModal(false)} style={styles.filterCloseBtn}>
                <MIcon name="close" size={18} color="#6B7280" />
              </TouchableOpacity>
            </View>

            <ScrollView showsVerticalScrollIndicator={false}>
              <Text style={styles.filterLabel}>Schedule</Text>
              <View style={styles.filterChipRow}>
                {SCHEDULES.map(s => (
                  <TouchableOpacity key={s} style={[styles.filterChip, schedule === s && styles.filterChipActive]} onPress={() => setSchedule(s)}>
                    <Text style={[styles.filterChipText, schedule === s && styles.filterChipTextActive]}>{s}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              {schedule === 'CUSTOM' && (
                <View style={{ marginBottom: 16, backgroundColor: '#141E18', padding: 12, borderRadius: 10, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)' }}>
                  <Text style={[styles.filterLabel, { marginBottom: 8 }]}>Custom Date Range</Text>
                  <View style={{ flexDirection: 'row', gap: 10 }}>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 11, fontWeight: '600', color: '#64748B', marginBottom: 4 }}>Start Date</Text>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e: any) => setCustomStartDate(e.target.value)}
                        style={dateInputStyle}
                      />
                    </View>
                    <View style={{ flex: 1 }}>
                      <Text style={{ fontSize: 11, fontWeight: '600', color: '#64748B', marginBottom: 4 }}>End Date</Text>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e: any) => setCustomEndDate(e.target.value)}
                        style={dateInputStyle}
                      />
                    </View>
                  </View>
                </View>
              )}

              <Text style={styles.filterLabel}>Status</Text>
              <View style={styles.filterChipRow}>
                {STATUS_OPTIONS.map(s => (
                  <TouchableOpacity key={s} style={[styles.filterChip, statusFilter === s && styles.filterChipActive]} onPress={() => setStatusFilter(s)}>
                    <Text style={[styles.filterChipText, statusFilter === s && styles.filterChipTextActive]}>{s}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              <Text style={styles.filterLabel}>Restaurant / Seller</Text>
              <select value={sellerFilter} onChange={(e: any) => setSellerFilter(e.target.value)} style={selectStyle}>
                <option value="ALL">All Restaurants ({availableSellers.length})</option>
                {availableSellers.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>

              <Text style={[styles.filterLabel, { marginTop: 16 }]}>Delivery Partner</Text>
              <select value={dpFilter} onChange={(e: any) => setDpFilter(e.target.value)} style={selectStyle}>
                <option value="ALL">All Partners ({availablePartners.length})</option>
                {availablePartners.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>

              <Text style={[styles.filterLabel, { marginTop: 16 }]}>Payment Method</Text>
              <View style={styles.filterChipRow}>
                {['ALL', ...availablePayments].map(p => (
                  <TouchableOpacity key={p} style={[styles.filterChip, paymentFilter === p && styles.filterChipActive]} onPress={() => setPaymentFilter(p)}>
                    <Text style={[styles.filterChipText, paymentFilter === p && styles.filterChipTextActive]}>{p}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </ScrollView>

            <TouchableOpacity style={styles.applyButton} onPress={() => setFilterModal(false)}>
              <Text style={styles.applyButtonText}>Apply Filters ({visible.length})</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Customer profile drawer (right side) */}
      <Modal transparent visible={!!profileOrder} animationType="fade" onRequestClose={() => setProfileOrder(null)}>
        <View style={styles.drawerOverlay}>
          <Pressable style={styles.drawerBackdrop} onPress={() => setProfileOrder(null)} />
          <Animated.View
            style={[styles.drawerSheet, isMobile && { width: '88%' }, { transform: [{ translateX: drawerX }] }]}
          >
            {profile && (
              <>
                {/* Header */}
                <View style={styles.drawerHeader}>
                  <View style={styles.drawerAvatar}>
                    <MIcon name="person" size={28} color="#fff" />
                  </View>
                  <View style={{ flex: 1, marginLeft: 12 }}>
                    <Text style={styles.drawerName} numberOfLines={1}>{profileName || profile.loc?.name || 'Customer'}</Text>
                    {profileName && profile.loc?.name && profileName !== profile.loc?.name && (
                      <Text style={styles.drawerSub} numberOfLines={1}>Order name: {profile.loc?.name}</Text>
                    )}
                    <Text style={styles.drawerSub} numberOfLines={1}>{profile.cid ?? '—'}</Text>
                  </View>
                  <TouchableOpacity onPress={() => setProfileOrder(null)} style={styles.drawerClose}>
                    <MIcon name="close" size={20} color="#A8C4AF" />
                  </TouchableOpacity>
                </View>

                <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 24 }}>
                  {/* Contact / address */}
                  <View style={styles.drawerInfoCard}>
                    <View style={styles.drawerInfoRow}>
                      <MIcon name="call" size={16} color="#10B981" />
                      <Text style={styles.drawerInfoText}>{profile.loc?.secondaryContact || profile.loc?.contact || 'N/A'}</Text>
                    </View>
                    <View style={styles.drawerInfoRow}>
                      <MIcon name="place" size={16} color="#EF4444" />
                      <Text style={styles.drawerInfoText} numberOfLines={3}>{profile.loc?.address ?? 'N/A'}</Text>
                    </View>
                    <View style={styles.drawerInfoRow}>
                      <MIcon name="schedule" size={16} color="#6B7280" />
                      <Text style={styles.drawerInfoText}>Last order {fmtDate(profile.lastOrderAt)}</Text>
                    </View>
                  </View>

                  {/* Notify customer — "unable to reach you" */}
                  <TouchableOpacity
                    onPress={notifyCustomer}
                    disabled={notifying}
                    style={{
                      flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                      gap: 8, backgroundColor: notifying ? '#FBBF24' : '#F59E0B',
                      borderRadius: 12, paddingVertical: 12, marginTop: 14, marginBottom: 14,
                    }}
                  >
                    {notifying
                      ? <ActivityIndicator size="small" color="#fff" />
                      : <MIcon name="notifications_active" size={18} color="#fff" />}
                    <Text style={{ color: '#fff', fontWeight: '700', fontSize: 14 }}>
                      {notifying ? 'Sending…' : 'Notify: unable to reach you'}
                    </Text>
                  </TouchableOpacity>

                  {/* Stats */}
                  <View style={styles.drawerStatsRow}>
                    {[
                      { label: 'Orders', value: String(profile.count), color: '#21808D' },
                      { label: 'Spent', value: `₹${profile.totalSpent.toFixed(0)}`, color: '#10B981' },
                      { label: 'Delivered', value: String(profile.delivered), color: '#3B82F6' },
                      { label: 'Cancelled', value: String(profile.cancelled), color: '#EF4444' },
                    ].map(s => (
                      <View key={s.label} style={styles.drawerStat}>
                        <Text style={[styles.drawerStatValue, { color: s.color }]}>{s.value}</Text>
                        <Text style={styles.drawerStatLabel}>{s.label}</Text>
                      </View>
                    ))}
                  </View>

                  {/* Order history */}
                  <Text style={styles.drawerSectionTitle}>Order History ({profile.orders.length})</Text>
                  {profile.orders.map(o => {
                    const color = STATUS_COLOR[(o.status || '').toUpperCase()] ?? '#6B7280';
                    return (
                      <TouchableOpacity key={o.id} style={styles.drawerOrderRow} onPress={() => setItemsModalOrder(o)}>
                        <View style={[styles.drawerOrderDot, { backgroundColor: color }]} />
                        <View style={{ flex: 1 }}>
                          <Text style={styles.drawerOrderNo}>#{lastOrderNo(o)}</Text>
                          <Text style={styles.drawerOrderDate}>{fmtDate(o.createdAt)} · {o.sellerLocation?.name ?? '—'}</Text>
                        </View>
                        <View style={{ alignItems: 'flex-end' }}>
                          <Text style={styles.drawerOrderAmt}>₹{(o.customerPaid || 0).toFixed(0)}</Text>
                          <Text style={[styles.drawerOrderStatus, { color }]}>{o.status ?? 'N/A'}</Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </>
            )}
          </Animated.View>
        </View>
      </Modal>

      {/* Status update menu (single, screen-level) */}
      <Modal transparent visible={!!statusMenuOrder} animationType="fade" onRequestClose={() => setStatusMenuId(null)}>
        <Pressable style={styles.menuOverlay} onPress={() => setStatusMenuId(null)}>
          <Pressable style={styles.menuContainer} onPress={() => {}}>
            <Text style={styles.menuTitle}>Update Status</Text>
            {STATUS_OPTIONS.filter(s => s !== 'ALL').map(s => {
              const color = STATUS_COLOR[s] ?? '#374151';
              return (
                <TouchableOpacity key={s} style={styles.menuItem} onPress={() => statusMenuOrder && updateStatus(statusMenuOrder, s)}>
                  <View style={[styles.menuDot, { backgroundColor: color }]} />
                  <Text style={[styles.menuText, { color }]}>{s}</Text>
                </TouchableOpacity>
              );
            })}
            <View style={{ height: 1, backgroundColor: '#E5E7EB', marginVertical: 6 }} />
            <TouchableOpacity
              style={styles.menuItem}
              onPress={() => statusMenuOrder && deleteOrder(statusMenuOrder)}
            >
              <MIcon name="delete_outline" size={18} color="#EF4444" />
              <Text style={[styles.menuText, { color: '#EF4444', marginLeft: 8 }]}>Delete order</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Items Modal */}
      <Modal transparent visible={!!itemsModalOrder} animationType="fade" onRequestClose={() => setItemsModalOrder(null)}>
        <Pressable style={styles.itemsModalOverlay} onPress={() => setItemsModalOrder(null)}>
          <Pressable style={styles.itemsModalSheet} onPress={() => {}}>
            <View style={styles.itemsModalHeader}>
              <View style={{ flex: 1 }}>
                <Text style={styles.itemsModalTitle}>#{itemsModalOrder?.orderNumber?.[0] ?? 'Order'}</Text>
                <Text style={styles.itemsModalSub}>{itemsModalOrder?.sellerLocation?.name ?? ''}</Text>
              </View>
              <TouchableOpacity onPress={() => setItemsModalOrder(null)} style={{ padding: 4 }}>
                <MIcon name="close" size={22} color="#6B7280" />
              </TouchableOpacity>
            </View>
            <View style={styles.itemsModalTableHeader}>
              <Text style={[styles.itemsModalHeaderCell, { flex: 4 }]}>Product</Text>
              <Text style={[styles.itemsModalHeaderCell, { flex: 1, textAlign: 'center' }]}>Qty</Text>
              <Text style={[styles.itemsModalHeaderCell, { flex: 1, textAlign: 'right' }]}>Price</Text>
              <Text style={[styles.itemsModalHeaderCell, { flex: 1, textAlign: 'right' }]}>Total</Text>
              <View style={{ width: 32 }} />
            </View>
            <ScrollView style={{ maxHeight: 380 }} showsVerticalScrollIndicator={false}>
              {itemsModalOrder?.orderItems?.map((item, idx) => (
                <View key={idx} style={[styles.itemsModalRow, idx % 2 === 0 && { backgroundColor: '#F9FAFB' }]}>
                  <View style={{ flex: 4, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    {item.image ? (
                      <Image source={{ uri: item.image }} style={styles.itemsModalImage} resizeMode="cover" />
                    ) : (
                      <View style={[styles.itemsModalImage, { alignItems: 'center', justifyContent: 'center' }]}>
                        <MIcon name="image" size={20} color="#CBD5E1" />
                      </View>
                    )}
                    <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                      {item.isVeg != null && (
                        <MIcon name="fiber_manual_record" size={12} color={item.isVeg ? '#16a34a' : '#dc2626'} />
                      )}
                      <Text style={styles.itemsModalProductName} numberOfLines={2}>
                        {item.productName ?? item.name ?? 'No Name'}
                      </Text>
                    </View>
                  </View>
                  <Text style={[styles.itemsModalCell, { flex: 1, textAlign: 'center' }]}>×{item.quantity ?? item.count ?? 1}</Text>
                  <Text style={[styles.itemsModalCell, { flex: 1, textAlign: 'right' }]}>₹{(item.price ?? item.unitPrice ?? 0).toFixed(2)}</Text>
                  <Text style={[styles.itemsModalCell, { flex: 1, textAlign: 'right', fontWeight: '700', color: '#10B981' }]}>
                    ₹{((item.price ?? item.unitPrice ?? 0) * (item.quantity ?? item.count ?? 1)).toFixed(2)}
                  </Text>
                  <TouchableOpacity
                    onPress={() => itemsModalOrder && deleteOrderItem(itemsModalOrder, idx)}
                    disabled={busyId === itemsModalOrder?.id}
                    style={{ width: 32, alignItems: 'center', justifyContent: 'center', paddingLeft: 4 }}
                    hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  >
                    <MIcon name="delete_outline" size={18} color="#EF4444" />
                  </TouchableOpacity>
                </View>
              ))}
            </ScrollView>
            <View style={styles.itemsModalFooter}>
              <Text style={styles.itemsModalFooterLabel}>Order Total</Text>
              <Text style={styles.itemsModalFooterValue}>₹{(itemsModalOrder?.totalPrice || 0).toFixed(2)}</Text>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
}

const selectStyle: React.CSSProperties = {
  width: '100%',
  height: 42,
  backgroundColor: '#F9FAFB',
  border: '1px solid #E5E7EB',
  borderRadius: 8,
  paddingLeft: 12,
  paddingRight: 12,
  fontSize: 13,
  color: '#111827',
  cursor: 'pointer',
  outline: 'none',
};

const dateInputStyle: React.CSSProperties = {
  width: '100%',
  height: 38,
  backgroundColor: '#FFFFFF',
  border: '1px solid #CBD5E1',
  borderRadius: 8,
  paddingLeft: 10,
  paddingRight: 10,
  fontSize: 13,
  color: '#1E293B',
  outline: 'none',
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#0E1512', padding: 10 },

  // Fixed bottom calculation bar
  bottomBar: {
    position: 'absolute', left: 0, right: 0, bottom: 0,
    flexDirection: 'row', alignItems: 'center',
    backgroundColor: '#17201B', paddingVertical: 10,
    borderTopWidth: 1, borderTopColor: '#E5E7EB',
    shadowColor: '#000', shadowOffset: { width: 0, height: -2 }, shadowOpacity: 0.08, shadowRadius: 6, elevation: 8,
  },
  bottomBarMobile: { justifyContent: 'space-around', paddingHorizontal: 8 },
  bottomBarDesktop: { paddingHorizontal: 14 }, // matches container padding(10) + row padding(4) so totals sit under their columns
  bottomBarItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  bottomBarValue: { fontSize: 14, fontWeight: '800' },

  // Toolbar
  toolbar: { flexDirection: 'row', alignItems: 'center', marginHorizontal: -10, marginTop: -10, marginBottom: 8, gap: 6, flexWrap: 'wrap', backgroundColor: '#0A1712', paddingHorizontal: 12, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(33,128,141,0.18)' },
  toolbarTitle: { fontSize: 14, fontWeight: '800', color: '#fff' },
  toolbarSub: { fontSize: 10, color: '#8BAAB8', marginTop: 1 },
  navButton: { width: 30, height: 34, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(33,128,141,0.2)', borderRadius: 7, borderWidth: 1, borderColor: 'rgba(33,128,141,0.4)' },
  dateChip: { backgroundColor: 'rgba(255,255,255,0.07)', borderRadius: 7, paddingHorizontal: 10, height: 34, justifyContent: 'center', borderWidth: 1, borderColor: 'rgba(33,128,141,0.3)' },
  currentDate: { fontSize: 12, fontWeight: '600', color: '#e2f0f2' },
  searchWrap: {
    flexDirection: 'row', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 7,
    borderWidth: 1, borderColor: 'rgba(33,128,141,0.25)', paddingHorizontal: 8, height: 34, minWidth: 160,
  },
  toolbarSearch: { flex: 1, height: 34, fontSize: 13, color: '#e2f0f2', outlineStyle: 'none' } as any,
  statusChipRow: { flex: 1, flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 5 },
  statusChip: { height: 34, paddingHorizontal: 12, borderRadius: 5, backgroundColor: 'transparent', borderWidth: 1, borderColor: 'rgba(33,128,141,0.3)', justifyContent: 'center', alignItems: 'center' },
  statusChipText: { fontSize: 10, fontWeight: '700', color: '#8BAAB8', letterSpacing: 0.3 },
  filterButton: { width: 34, height: 34, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.06)', borderRadius: 7, borderWidth: 1, borderColor: 'rgba(33,128,141,0.3)' },
  filterBadge: { position: 'absolute', top: -4, right: -4, backgroundColor: '#EF4444', borderRadius: 8, minWidth: 16, height: 16, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 3 },
  filterBadgeText: { color: '#fff', fontSize: 9, fontWeight: '700' },
  exportButton: { flexDirection: 'row', alignItems: 'center', height: 34, paddingHorizontal: 10, backgroundColor: '#21C98E', borderRadius: 7 },
  exportButtonText: { color: '#fff', fontSize: 11, fontWeight: '700', letterSpacing: 0.3 },
  statementButton: { flexDirection: 'row', alignItems: 'center', height: 34, paddingHorizontal: 10, backgroundColor: '#21808D', borderRadius: 7 },

  // Filter modal
  filterModalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'flex-end' },
  filterModalSheet: { backgroundColor: '#17201B', borderTopLeftRadius: 20, borderTopRightRadius: 20, paddingHorizontal: 18, paddingTop: 14, paddingBottom: 28, maxHeight: '85%' },
  filterModalHeader: { flexDirection: 'row', alignItems: 'center', marginBottom: 16 },
  filterModalTitle: { flex: 1, fontSize: 15, fontWeight: '700', color: '#111827' },
  resetButton: { paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, backgroundColor: '#0E1512', marginRight: 8 },
  resetButtonText: { fontSize: 12, color: '#6B7280', fontWeight: '600' },
  filterCloseBtn: { width: 28, height: 28, borderRadius: 14, backgroundColor: '#0E1512', alignItems: 'center', justifyContent: 'center' },
  filterLabel: { fontSize: 10, fontWeight: '700', color: '#6A8A70', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.8 },

  // Filter chips
  filterChipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginBottom: 16 },
  filterChip: { paddingHorizontal: 14, paddingVertical: 7, borderRadius: 16, backgroundColor: '#F9FAFB', borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)' },
  filterChipActive: { backgroundColor: '#1D4ED8', borderColor: '#1D4ED8' },
  filterChipText: { fontSize: 11, fontWeight: '600', color: '#A8C4AF' },
  filterChipTextActive: { color: '#fff' },
  applyButton: { backgroundColor: '#1D4ED8', borderRadius: 10, paddingVertical: 12, alignItems: 'center', marginTop: 6 },
  applyButtonText: { color: '#fff', fontSize: 13, fontWeight: '700' },

  // Table
  tableHeader: { flexDirection: 'row', backgroundColor: '#1e293b', paddingVertical: 9, paddingHorizontal: 4, borderRadius: 8, marginBottom: 2 },
  headerText: { color: '#94A3B8', fontWeight: '700', textAlign: 'center', fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5 },
  tableRow: { flexDirection: 'row', backgroundColor: '#17201B', borderBottomWidth: 1, borderColor: 'rgba(255,255,255,0.07)', paddingVertical: 9, paddingHorizontal: 4, alignItems: 'center' },
  rowData: { flex: 13.4, flexDirection: 'row', alignItems: 'center', cursor: 'pointer' } as any,
  rowActions: { flex: 2.1, flexDirection: 'row', alignItems: 'center' },
  tableRowAlt: { backgroundColor: '#141E18' },
  tableRowHover: { backgroundColor: '#EFF6FF' },
  tableCell: { fontSize: 11, color: '#A8C4AF', textAlign: 'center', paddingHorizontal: 4 },
  cellSno: { flex: 0.5, fontWeight: '600', color: '#6B7280' },
  cellOrder: { flex: 2, textAlign: 'left' },
  cellContact: { flex: 1.4 },
  cellStatus: { flex: 1.3 },
  cellDate: { flex: 1.6, fontSize: 10 },
  cellPrice: { flex: 1, fontSize: 11 },
  cellExpand: { flex: 0.7, alignItems: 'center', justifyContent: 'center' },

  // Status pill
  statusPill: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 7, paddingVertical: 3, borderRadius: 20, borderWidth: 1, gap: 4 },
  statusDot: { width: 5, height: 5, borderRadius: 3 },
  statusPillText: { fontSize: 9, fontWeight: '700', letterSpacing: 0.3 },

  // Mobile card
  card: { backgroundColor: '#17201B', borderRadius: 10, marginBottom: 8, overflow: 'hidden', flexDirection: 'row', shadowColor: '#000', shadowOffset: { width: 0, height: 1 }, shadowOpacity: 0.06, shadowRadius: 3, elevation: 2 },
  cardAccent: { width: 4 },
  cardInner: { flex: 1, padding: 12 },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 },
  cardOrderNum: { fontSize: 13, fontWeight: '700', color: '#1D4ED8' },
  cardGrid: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 8, gap: 6 },
  cardGridItem: { flex: 1, minWidth: '45%', backgroundColor: '#F9FAFB', borderRadius: 6, padding: 6 },
  cardRow: { flexDirection: 'row', marginBottom: 4 },
  cardLabel: { fontSize: 10, color: '#6A8A70', fontWeight: '600', width: 60, textTransform: 'uppercase' },
  cardValue: { fontSize: 11, color: '#1F2937', flex: 1 },
  cardActions: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 10, paddingTop: 8, borderTopWidth: 1, borderTopColor: '#F1F5F9' },
  expandButton: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#EFF6FF', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, borderWidth: 1, borderColor: '#BFDBFE' },
  expandButtonText: { color: '#3B82F6', fontWeight: '600', fontSize: 11 },
  mapButton: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#F0FDF4', paddingHorizontal: 10, paddingVertical: 5, borderRadius: 6, borderWidth: 1, borderColor: '#BBF7D0' },
  mapButtonText: { color: '#10B981', fontWeight: '600', fontSize: 11 },
  statusBtn: { width: 32, height: 28, alignItems: 'center', justifyContent: 'center', borderRadius: 6, backgroundColor: '#0E1512' },

  // Status menu
  menuOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.3)', justifyContent: 'center', alignItems: 'center' },
  menuContainer: { backgroundColor: '#17201B', borderRadius: 12, paddingTop: 12, paddingBottom: 8, width: 220, shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.15, shadowRadius: 8, elevation: 8 },
  menuTitle: { fontSize: 11, fontWeight: '700', color: '#6A8A70', textAlign: 'center', textTransform: 'uppercase', letterSpacing: 0.5, paddingBottom: 10 },
  menuItem: { flexDirection: 'row', alignItems: 'center', paddingVertical: 11, paddingHorizontal: 16, gap: 10 },
  menuDot: { width: 8, height: 8, borderRadius: 4 },
  menuText: { fontWeight: '700', fontSize: 13 },

  // Items modal
  itemsModalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', justifyContent: 'center', alignItems: 'center', padding: 20 },
  itemsModalSheet: { backgroundColor: '#17201B', borderRadius: 16, width: '100%', maxWidth: 480, overflow: 'hidden', shadowColor: '#000', shadowOpacity: 0.2, shadowRadius: 20, elevation: 10 },
  itemsModalHeader: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 20, paddingTop: 18, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  itemsModalTitle: { fontSize: 15, fontWeight: '700', color: '#1e293b' },
  itemsModalSub: { fontSize: 12, color: '#64748B', marginTop: 2 },
  itemsModalTableHeader: { flexDirection: 'row', backgroundColor: '#141E18', paddingHorizontal: 16, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  itemsModalHeaderCell: { fontSize: 11, fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5 },
  itemsModalRow: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 16, paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F1F5F9' },
  itemsModalImage: { width: 48, height: 48, borderRadius: 8, backgroundColor: '#F1F5F9' },
  itemsModalProductName: { fontSize: 13, fontWeight: '600', color: '#1e293b', flex: 1 },
  itemsModalCell: { fontSize: 13, color: '#8BAAB8' },
  itemsModalFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: 20, paddingVertical: 14, backgroundColor: '#141E18', borderTopWidth: 1, borderTopColor: '#E2E8F0' },
  itemsModalFooterLabel: { fontSize: 13, fontWeight: '700', color: '#64748B' },
  itemsModalFooterValue: { fontSize: 16, fontWeight: '800', color: '#10B981' },

  // Customer profile drawer (right side)
  drawerOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.45)', flexDirection: 'row', justifyContent: 'flex-end' },
  drawerBackdrop: { flex: 1, height: '100%' },
  drawerSheet: { width: 380, height: '100%', backgroundColor: '#17201B', paddingHorizontal: 18, paddingTop: 18, shadowColor: '#000', shadowOffset: { width: -2, height: 0 }, shadowOpacity: 0.15, shadowRadius: 12, elevation: 12 },
  drawerHeader: { flexDirection: 'row', alignItems: 'center', paddingBottom: 16, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.08)' },
  drawerAvatar: { width: 48, height: 48, borderRadius: 24, backgroundColor: '#1D4ED8', alignItems: 'center', justifyContent: 'center' },
  drawerName: { fontSize: 16, fontWeight: '800', color: '#F1F5F9' },
  drawerSub: { fontSize: 11, color: '#6A8A70', marginTop: 2 },
  drawerClose: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#0E1512', alignItems: 'center', justifyContent: 'center' },

  drawerInfoCard: { backgroundColor: '#1E2B22', borderRadius: 10, padding: 12, marginTop: 16, gap: 10 },
  drawerInfoRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 8 },
  drawerInfoText: { flex: 1, fontSize: 12, color: '#A8C4AF', lineHeight: 17 },

  drawerStatsRow: { flexDirection: 'row', gap: 8, marginTop: 14 },
  drawerStat: { flex: 1, backgroundColor: '#1E2B22', borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', borderRadius: 10, paddingVertical: 10, alignItems: 'center' },
  drawerStatValue: { fontSize: 16, fontWeight: '800' },
  drawerStatLabel: { fontSize: 9, color: '#6A8A70', fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 3 },

  drawerSectionTitle: { fontSize: 11, fontWeight: '700', color: '#6A8A70', textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 22, marginBottom: 8 },
  drawerOrderRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.07)', gap: 10, cursor: 'pointer' } as any,
  drawerOrderDot: { width: 8, height: 8, borderRadius: 4 },
  drawerOrderNo: { fontSize: 13, fontWeight: '700', color: '#60A5FA' },
  drawerOrderDate: { fontSize: 10, color: '#6A8A70', marginTop: 2 },
  drawerOrderAmt: { fontSize: 13, fontWeight: '800', color: '#F1F5F9' },
  drawerOrderStatus: { fontSize: 9, fontWeight: '700', marginTop: 2 },

  // Empty
  emptyBox: { alignItems: 'center', marginTop: 60 },
  emptyText: { fontSize: 13, color: '#6A8A70', fontWeight: '500', marginTop: 10 },
});

// Native <input>/<select> styles (raw DOM elements, not RN styles)

// ── Statement drawer styles ───────────────────────────────────────
const sd = StyleSheet.create({
  root: { flex: 1, flexDirection: 'row', justifyContent: 'flex-end' },
  backdrop: { ...StyleSheet.absoluteFillObject, backgroundColor: 'rgba(15,23,42,0.45)' },
  drawer: { height: '100%', backgroundColor: '#0E1512', shadowColor: '#000', shadowOffset: { width: -4, height: 0 }, shadowOpacity: 0.18, shadowRadius: 20, elevation: 16 },
  header: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#17201B', paddingHorizontal: 16, paddingTop: 16, paddingBottom: 14, borderBottomWidth: 1, borderBottomColor: '#e2e8f0', gap: 12 },
  closeBtn: { width: 32, height: 32, borderRadius: 16, backgroundColor: '#f1f5f9', justifyContent: 'center', alignItems: 'center' },
  closeTxt: { fontSize: 14, color: '#7A9A80', fontWeight: '600' },
  title: { flex: 1, fontSize: 17, fontWeight: '700', color: '#E2EDE6', textAlign: 'center' },
  label: { fontSize: 11, fontWeight: '800', color: '#7A9A80', letterSpacing: 0.5, textTransform: 'uppercase', marginTop: 16, marginBottom: 7 },
  segRow: { flexDirection: 'row', gap: 8 },
  seg: { flex: 1, paddingVertical: 10, borderRadius: 9, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', backgroundColor: '#17201B', alignItems: 'center' },
  segOn: { backgroundColor: '#21808D', borderColor: '#21808D' },
  segTxt: { fontSize: 12, fontWeight: '700', color: '#7A9A80' },
  segTxtOn: { color: '#fff' },
  row2: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  textInput: { borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 13, color: '#E2EDE6', backgroundColor: '#17201B' },
  colGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  colChip: { paddingHorizontal: 11, paddingVertical: 7, borderRadius: 16, borderWidth: 1, borderColor: 'rgba(255,255,255,0.09)', backgroundColor: '#17201B' },
  colChipOn: { backgroundColor: '#e8f5f7', borderColor: '#21808D' },
  colChipTxt: { fontSize: 12, fontWeight: '600', color: '#7A9A80' },
  colChipTxtOn: { color: '#21808D' },
  note: { fontSize: 11, color: '#7A9A80', marginTop: 16, lineHeight: 16 },
  footer: { padding: 16, borderTopWidth: 1, borderTopColor: '#e2e8f0', backgroundColor: '#17201B' },
  downloadBtn: { backgroundColor: '#21808D', borderRadius: 10, paddingVertical: 14, alignItems: 'center' },
  downloadTxt: { color: '#fff', fontWeight: '700', fontSize: 15 },
});

// ── Order detail drawer styles ────────────────────────────────────
const od = StyleSheet.create({
  topRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 },
  custBtn: { backgroundColor: '#EFF6FF', borderRadius: 8, paddingHorizontal: 10, paddingVertical: 6, borderWidth: 1, borderColor: '#BFDBFE' },
  custBtnTxt: { color: '#2563EB', fontWeight: '700', fontSize: 11 },
  sec: { fontSize: 11, fontWeight: '800', color: '#7A9A80', letterSpacing: 0.6, textTransform: 'uppercase', marginTop: 18, marginBottom: 8, borderBottomWidth: 1, borderBottomColor: '#eef2f6', paddingBottom: 5 },
  kv: { flexDirection: 'row', justifyContent: 'space-between', gap: 12, paddingVertical: 4 },
  k: { fontSize: 12, color: '#5A7A60', fontWeight: '600' },
  v: { fontSize: 13, color: '#E2EDE6', flexShrink: 1, textAlign: 'right', fontWeight: '500' },
  empty: { fontSize: 12, color: '#5A7A60', fontStyle: 'italic' },
  itemRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#f1f5f9' },
  itemName: { flex: 1, fontSize: 13, color: '#E2EDE6' },
  itemQty: { fontSize: 12, color: '#7A9A80' },
  itemTot: { fontSize: 13, fontWeight: '700', color: '#E2EDE6', minWidth: 56, textAlign: 'right' },
  vegDot: { width: 14, height: 14, borderRadius: 3, borderWidth: 1.5, justifyContent: 'center', alignItems: 'center' },
  vegDotInner: { width: 6, height: 6, borderRadius: 3 },
  totalRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: '#e2e8f0' },
  totalK: { fontSize: 14, fontWeight: '800', color: '#E2EDE6' },
  totalV: { fontSize: 16, fontWeight: '800', color: '#10B981' },
});
